import type { ID } from '@/domain/types/common';
import type { PetInstance } from '@/domain/types/pet';
import type { EggInstance } from '@/domain/types/egg';
import type { PlayerProfile } from '@/domain/types/avatar';
import type { ActiveEffects, PotionsBag } from '@/config/potions.config';
import { STARTING_CURRENCIES, STARTER_SEED } from '@/config/balance.config';
import { DEFAULT_STARTER_SKIN_ID, DEFAULT_UNLOCKED_SKIN_IDS } from '@/config/avatars.config';
import { ENABLED_BIOMES } from '@/config/biomes.config';
import type { Coins } from '@/domain/economy/coins';

/**
 * Wersja schematu zapisu. Inkrementuj o 1 przy KAŻDEJ zmianie struktury
 * `PlayerSave` i dopisz migrację w `src/save/migrations/`.
 *
 * **v2 (sesja 30):** dodane onboarding flags `starterPetsChosen` i
 * `hasSeenFabTutorial`. Migration v1→v2 ustawia oba na `true` dla istniejących
 * graczy którzy mieli już progress (inventory/equipped/petsHatched > 0) i
 * `false` dla pustych v1 saves (fresh, ale stuck pre-avatar).
 *
 * **v3 (mikstury):** dodane `potions` (plecak mikstur defId→ilość) i
 * `activeEffects` (aktywne buffy czasowe). Migration v2→v3 ustawia oba puste.
 *
 * **v4 (eventy):** dodany `eventCurrencies` — generyczny portfel walut
 * eventowych (id→ilość, np. `event_shells`). Migration v3→v4 ustawia `{}`.
 *
 * **v5 (duże liczby):** `currencies.coins` i `stats.coinsEarnedTotal` są
 * decimal stringami. Dzięki temu JSON/IndexedDB nie tracą cyfr powyżej 2^53.
 */
export const SAVE_VERSION = 5;

/**
 * Pełny stan gracza, zapisywany na dysku (IndexedDB) i docelowo w cloud.
 *
 * Reguły:
 *  - WSZYSTKO co ma przeżyć refresh strony JEST w PlayerSave.
 *  - Schemat ewoluuje przez migracje (każda zmiana = bump `SAVE_VERSION`).
 *  - Pola muszą być serializowalne JSON (brak funkcji, klas, Map/Set).
 *
 * Patrz `GAME_DESIGN.md §7` i `§5.1.1 Avatar System` po pełną dokumentację.
 */
export interface PlayerSave {
  /** Wersja schematu — używana przez migracje. */
  version: number;
  /** Epoch ms ostatniego zapisu. */
  lastSavedAt: number;

  /** Profil gracza (uid, nick, avatar, lista odblokowanych skinów). */
  profile: PlayerProfile;

  /** Stan walut stałych (coins/gems/...). */
  currencies: { coins: Coins; gems: number; event_hearts: number };

  /**
   * Generyczny portfel walut EVENTOWYCH (id → ilość, np. `event_shells`).
   * Każdy event deklaruje swoją walutę w `events.config.ts` (`EventCurrencyDef`);
   * tu trzymamy tylko salda. Server-authoritative (kredytowane endpointami eventu).
   * Pusta mapa = gracz nie ma żadnej waluty eventowej. Patrz `EventCurrencyDef`.
   */
  eventCurrencies: Record<string, number>;

  /** Wyklute pety (inventory). */
  inventory: PetInstance[];

  /** Niewyklute jajka (osobny inventory). */
  eggs: EggInstance[];

  /** UIDs equipped petów. Max długość = `PET_SLOTS.maxCount`. */
  equipped: string[];

  /** Lista odblokowanych biomów (po ID). */
  biomesUnlocked: ID[];

  /** Aktualnie aktywny biome. */
  currentBiomeId: ID;

  /** Poziomy upgrade'ów (id → level). */
  upgrades: Record<ID, number>;

  /** Statystyki jajek (otwarte total, jajka od ostatniego huge per typ). */
  eggStats: Record<ID, { openedTotal: number; rollsSinceHuge: number }>;

  /**
   * Stan eventów per `eventId`. `optIn`/`progress` zarezerwowane (nieużywane).
   * `album` (EV-3): trwały ślad kolekcji — `discovered` to odkryte petIds (pet
   * odkryty po pierwszym wykluciu, NIE zużywany), `claimed` = czy odebrano nagrodę
   * za komplet. Server-authoritative (aktualizowane przy hatchu + claimie).
   * `dailyTrader`: eventowa dzienna paczka od NPC; też server-authoritative
   * (status/claim przez `/events/:eventId/daily-trader/*`).
   * `pierFishing`: mały cooldownowy połów z mola; server-authoritative
   * (claim przez `/events/:eventId/pier-fishing/claim`).
   */
  events: Record<
    ID,
    {
      optIn?: boolean;
      progress?: number;
      album?: { discovered: ID[]; claimed: boolean };
      dailyTrader?: {
        lastClaimDay?: string;
        lastClaimAt?: number;
        streakDays?: number;
        totalClaimed?: number;
      };
      pierFishing?: {
        lastClaimAt?: number;
        totalClaimed?: number;
      };
    }
  >;

  /** Tutoriale, easter eggi, niestandardowe flagi. */
  flags: Record<string, boolean | number>;

  /** Statystyki łączne (do achievementów). */
  stats: {
    objectsDestroyed: number;
    petsHatched: number;
    coinsEarnedTotal: Coins;
    playTimeSec: number;
  };

  /**
   * Plecak mikstur (defId → liczba sztuk). Kupowane za 💎 u handlarki, używane
   * z plecaka. Server-authoritative (mutowane WYŁĄCZNIE przez /potions/buy i
   * /potions/use). Patrz `src/config/potions.config.ts`.
   */
  potions: PotionsBag;

  /**
   * Aktywne buffy czasowe (stat → { mnożnik, kiedy wygasa }). Ustawiane przez
   * użycie mikstury (server-side `expiresAt`). Wygaszanie leniwe przy odczycie.
   */
  activeEffects: ActiveEffects;
}

/**
 * Tworzy świeży save dla nowego gracza.
 * Używa `STARTER_SEED` + `STARTING_CURRENCIES` z balance.config.
 *
 * Notes:
 *  - Sesja 30: tylko 1 starter egg (było 3). Gracz dostaje 2 pety w starter
 *    selector po wyborze avatara → 2 equipped + 1 egg = pełny cap (3 sloty).
 *  - `profile.avatar.skinId = DEFAULT_STARTER_SKIN_ID` (chłopiec) — placeholder
 *    do wyboru w AvatarSelectorModal.
 *  - `flags.avatarSelected/starterPetsChosen/hasSeenFabTutorial` = false →
 *    onboarding modale się pokażą.
 */
export function createInitialSave(): PlayerSave {
  const now = Date.now();
  const profileUid = newUid();

  // Spawn starter eggs (sesja 30: 1 zamiast 3)
  const starterEggs: EggInstance[] = STARTER_SEED.startingEggDefIds.map((defId) => ({
    uid: newUid(),
    defId,
    acquiredAt: now,
    source: 'gift' as const,
  }));

  // Spread STARTING_CURRENCIES (z `as const` więc trzeba przekonwertować)
  const currencies: PlayerSave['currencies'] = {
    coins: String(STARTING_CURRENCIES.coins),
    gems: STARTING_CURRENCIES.gems,
    event_hearts: STARTING_CURRENCIES.event_hearts,
  };

  // Upgrade levels z STARTER_SEED.startingUpgradeLevels (readonly literal)
  const upgrades: Record<ID, number> = {
    pet_slots: STARTER_SEED.startingUpgradeLevels.pet_slots,
    pet_storage: STARTER_SEED.startingUpgradeLevels.pet_storage,
    egg_storage: STARTER_SEED.startingUpgradeLevels.egg_storage,
    walk_speed: STARTER_SEED.startingUpgradeLevels.walk_speed,
    magnet_range: STARTER_SEED.startingUpgradeLevels.magnet_range,
    coin_mult: STARTER_SEED.startingUpgradeLevels.coin_mult,
    egg_luck: STARTER_SEED.startingUpgradeLevels.egg_luck,
  };

  return {
    version: SAVE_VERSION,
    lastSavedAt: now,
    profile: {
      uid: profileUid,
      createdAt: now,
      avatar: { skinId: DEFAULT_STARTER_SKIN_ID },
      // Domyślne skiny (boy/girl) odblokowane od startu — selektor „Wygląd" je pokazuje.
      // Serwer i tak traktuje je jako zawsze noszalne (mirror w serialize.ts).
      unlockedSkins: [...DEFAULT_UNLOCKED_SKIN_IDS],
    },
    currencies,
    // Świeży gracz nie ma żadnej waluty eventowej (zapełnia się dopiero
    // podczas trwającego eventu, server-authoritative).
    eventCurrencies: {},
    inventory: [],
    eggs: starterEggs,
    equipped: [],
    // Startowo: biom startowy + WSZYSTKIE biomy edukacyjne (darmowe, odblokowane
    // od startu). Liczone z configu → kolejny biom edukacyjny (Faza 2) odblokuje
    // się sam, bez zmian tutaj. Mirror server: STARTER_SAVE_FIELDS.biome_progress.
    biomesUnlocked: [
      STARTER_SEED.startingBiomeId,
      ...ENABLED_BIOMES.filter((b) => b.educational).map((b) => b.id),
    ],
    currentBiomeId: STARTER_SEED.startingBiomeId,
    upgrades,
    eggStats: {},
    events: {},
    // Sesja 30: trzy onboarding flags. Wszystkie `false` na start — fresh save
    // przejdzie:
    //   avatarSelected=false   → AvatarSelectorModal
    //   starterPetsChosen=false → StarterPetSelectorModal (po avatar)
    //   hasSeenFabTutorial=false → FabTutorialArrow (po starter pets)
    // Phaser (createGame) czeka aż `avatarSelected && starterPetsChosen` przez
    // `waitForOnboardingCompletion()` w main.tsx (sesja 30).
    flags: {
      avatarSelected: false,
      starterPetsChosen: false,
      hasSeenFabTutorial: false,
    },
    stats: {
      objectsDestroyed: 0,
      petsHatched: 0,
      coinsEarnedTotal: '0',
      playTimeSec: 0,
    },
    // Mikstury: świeży gracz startuje z pustym plecakiem i bez buffów.
    potions: {},
    activeEffects: {},
  };
}

/**
 * UID generator z hierarchią fallback'ów:
 *   1. `crypto.randomUUID()` — preferowane, RFC4122 v4 (Chrome 92+, Safari 15.4+, FF 95+)
 *   2. `crypto.getRandomValues()` — secure random bytes, manualnie do hex (starsze browsery)
 *   3. `Date.now() + counter + Math.random()` — last resort, NIE secure, mały kolizja risk
 *
 * Counter monotoniczny dodany do ostatecznego fallback'a żeby zmniejszyć kolizje
 * w przypadku szybkich kolejnych wywołań (np. createInitialSave dzwoni newUid()
 * kilka razy w tej samej ms).
 */
let fallbackCounter = 0;

function newUid(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // 32-znakowy hex string — collision-resistant
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  }
  // Last resort — non-crypto, ale z monotonicznym counterem
  fallbackCounter = (fallbackCounter + 1) & 0xffff;
  return `uid-${Date.now().toString(36)}-${fallbackCounter.toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
